import React from 'react'
import { Modal, Box, Typography, SxProps } from '@mui/material'
import ButtonBase from 'design-system/button'
import { Theme } from '@emotion/react'

/**
 * ContentModalProps type
 */
interface ContentModalProps extends React.PropsWithChildren {
  /** Whether the modal is open */
  open: boolean
  /** The title of the modal */
  title: string
  /** Optional description for the modal */
  description?: string
  /** Callback function for confirmation action */
  onConfirm: () => void
  /** Additional styles for the modal */
  sx?: SxProps<Theme>
}

/**
 * ContentModal component
 * This component renders a modal with a title, description, and content.
 * It allows for custom styling and includes a confirmation button.
 *
 * @param {ContentModalProps} props - The properties for the modal component.
 * @returns {JSX.Element} The rendered modal component.
 */
const ContentModal: React.FC<ContentModalProps> = props => {
  const { open, title, description, children, onConfirm, sx } = props

  return (
    <Modal open={open} onClose={onConfirm}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 300,
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
          textAlign: 'center',
          outline: 'none',
          ...sx
        }}
      >
        <Typography variant='h6' gutterBottom>
          {title}
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          {description}
        </Typography>
        {children}
        <Box
          display='flex'
          justifyContent='end'
          flexDirection={'row-reverse'}
          gap={1}
          mt={2}
        >
          <ButtonBase variant='contained' onClick={onConfirm}>
            Fechar
          </ButtonBase>
        </Box>
      </Box>
    </Modal>
  )
}

export default ContentModal
